/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import * as color from '@constant/color';
import { IAdminClozeData } from '@datatype/admin/api-type';
import ClozeItem from '@component/admin/cloze/cloze-home/cloze-list/cloze-item';
import Spinner from '@design-system/spinner';

interface IProps {
  clozes: IAdminClozeData[];
  selectedClozeKey: string | null;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  isError: boolean;
  handleSelect: (cloze: IAdminClozeData) => void;
  handleRetry: () => void;
  sentinelRef: React.RefObject<HTMLDivElement | null>;
}

const List = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
`;

const Loader = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px 0;
`;

const NextLoader = styled.div`
  display: flex;
  justify-content: center;
  padding: 16px 0;
`;

const Empty = styled.div`
  padding: 40px 0;
  text-align: center;
  color: ${color.BLACK_5};
`;

const Sentinel = styled.div`
  height: 1px;
`;

const ClozeList: React.FC<IProps> = ({
  clozes,
  selectedClozeKey,
  isLoading,
  isFetchingNextPage,
  isError,
  handleSelect,
  handleRetry,
  sentinelRef,
}) => {
  if (isLoading) {
    return (
      <Loader>
        <Spinner size='32px' color={color.BROWN_1} />
      </Loader>
    );
  }
  if (isError) {
    return (
      <Empty>
        데이터를 불러오지 못했습니다.{' '}
        <span
          style={{ color: color.BROWN_1, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={handleRetry}>
          다시 시도
        </span>
      </Empty>
    );
  }
  if (clozes.length === 0) {
    return <Empty>빈칸문제가 없습니다.</Empty>;
  }
  return (
    <>
      <List>
        {clozes.map((cloze) => (
          <ClozeItem
            key={cloze.key}
            cloze={cloze}
            isSelected={selectedClozeKey === cloze.key}
            handleClick={() => handleSelect(cloze)}
          />
        ))}
      </List>
      {isFetchingNextPage && (
        <NextLoader>
          <Spinner size='24px' color={color.BROWN_1} />
        </NextLoader>
      )}
      <Sentinel ref={sentinelRef} />
    </>
  );
};

export default ClozeList;